import { generalErrorText } from "./contants";
import { type AlertTypes } from "./types";

export enum BackendErrorCodesGeneral {
  UndefinedError = "general/undefined-error",
  Forbidden = "general/forbidden",
  NotFound = "general/not-found",
  TooManyRequests = "general/too-many-requests",
  InternalServerError = "general/internal-server-error",
  BadRequest = "general/bad-request",
  /** Firebase Storage / GCS object path empty, `.`, or path traversal after normalize. */
  InvalidStorageObjectPath = "general/invalid-storage-object-path",
}

export enum BackendErrorCodesForms {
  ValidationError = "forms/validation-error",
}

export enum BackendErrorCodesAuth {
  Unauthorized = "auth/unauthorized",
}

export enum BackendErrorCodesSubscription {
  InvalidCoupon = "subscription/invalid-coupon",
  CheckoutCreationFailed = "subscription/checkout-creation-failed",
  NoAnnualOrMonthlyProductFound = "subscription/no-annual-or-monthly-product-found",
  AlreadyHasActiveSubscription = "subscription/already-has-active-subscription",
}

export enum BackendErrorCodesGamification {
  CanonicalDurationMissing = "gamification/canonical-duration-missing",
  LessonFileRequired = "gamification/lesson-file-required",
  UnsupportedMediaType = "gamification/unsupported-media-type",
  HistoryImportTooFewLogs = "gamification/history-import-too-few-logs",
  HistoryImportPointsTooHigh = "gamification/history-import-points-too-high",
  CompletedAtOutOfRange = "gamification/completed-at-out-of-range",
  InvalidClientRequestId = "gamification/invalid-client-request-id",
  WatchedFilesMalformed = "gamification/watched-files-malformed",
  SessionResultMalformed = "gamification/session-result-malformed",
}

export enum BackendErrorCodesLegal {
  CancellationRequestNotFound = "legal/cancellation-request-not-found",
  CancellationRequestWrongType = "legal/cancellation-request-wrong-type",
  CancellationRequestNotConfirmable = "legal/cancellation-request-not-confirmable",
  CancellationRequestProcessing = "legal/cancellation-request-processing",
  CancellationRequiresManualProcessing = "legal/cancellation-requires-manual-processing",
  ConfirmationLinkExpired = "legal/confirmation-link-expired",
  ConfirmationLinkInvalid = "legal/confirmation-link-invalid",
  SessionExpired = "legal/session-expired",
  AccountEmailMissing = "legal/account-email-missing",
  AgreementDeclined = "legal/agreement-declined",
  OnboardingAlreadyCompleted = "legal/onboarding-already-completed",
}

export type BackendErrorCodes =
  | BackendErrorCodesGeneral
  | BackendErrorCodesForms
  | BackendErrorCodesAuth
  | BackendErrorCodesSubscription
  | BackendErrorCodesGamification
  | BackendErrorCodesLegal;

const allErrorEnums = [
  BackendErrorCodesGeneral,
  BackendErrorCodesForms,
  BackendErrorCodesAuth,
  BackendErrorCodesSubscription,
  BackendErrorCodesGamification,
  BackendErrorCodesLegal,
];

type BackendErrorCodeMessages = Record<BackendErrorCodes, { message: string; severity: AlertTypes }>;

// Define the messages object with type checking
export const backendErrorMessages: BackendErrorCodeMessages = {
  // General errors
  [BackendErrorCodesGeneral.UndefinedError]: {
    message: generalErrorText,
    severity: "danger",
  },
  [BackendErrorCodesGeneral.Forbidden]: {
    message: "Du hast nicht die Berechtigung, diese Aktion auszuführen.",
    severity: "danger",
  },
  [BackendErrorCodesGeneral.NotFound]: {
    message: "Die angeforderte Ressource wurde nicht gefunden.",
    severity: "danger",
  },
  [BackendErrorCodesGeneral.TooManyRequests]: {
    message: "Zu viele Anfragen. Bitte versuche es spater.",
    severity: "danger",
  },
  [BackendErrorCodesGeneral.InternalServerError]: {
    message: "Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es später oder kontaktiere uns.",
    severity: "danger",
  },
  [BackendErrorCodesGeneral.BadRequest]: {
    message: "Etwas stimmt nicht. Bitte versuche es erneut oder kontaktiere uns.",
    severity: "danger",
  },
  [BackendErrorCodesGeneral.InvalidStorageObjectPath]: {
    message: "Der Speicherpfad ist ungültig.",
    severity: "danger",
  },

  // Forms errors
  [BackendErrorCodesForms.ValidationError]: {
    message: "Etwas stimmt mit deinen Eingaben nicht. Bitte überprüfe deine Eingaben und versuche es erneut.",
    severity: "warning",
  },

  // Auth errors
  [BackendErrorCodesAuth.Unauthorized]: {
    message: "Du hast nicht die Berechtigung, diese Aktion auszuführen.",
    severity: "warning",
  },

  // Subscription errors
  [BackendErrorCodesSubscription.InvalidCoupon]: {
    message: "Der Rabattcode ist nicht gültig",
    severity: "warning",
  },
  [BackendErrorCodesSubscription.CheckoutCreationFailed]: {
    message: "Der Kaufprozess konnte nicht erstellt werden",
    severity: "danger",
  },
  [BackendErrorCodesSubscription.NoAnnualOrMonthlyProductFound]: {
    message: "No annual or monthly product found",
    severity: "danger",
  },
  [BackendErrorCodesSubscription.AlreadyHasActiveSubscription]: {
    message: "Du hast bereits eine Mitgliedschaft. Ein zweites Abo ist nicht nötig.",
    severity: "warning",
  },

  [BackendErrorCodesGamification.CanonicalDurationMissing]: {
    message: "Für diese Einheit fehlt die Dauer. Punkte können gerade nicht gutgeschrieben werden.",
    severity: "warning",
  },
  [BackendErrorCodesGamification.LessonFileRequired]: {
    message: "Diese Lektion konnte nicht zugeordnet werden.",
    severity: "warning",
  },
  [BackendErrorCodesGamification.UnsupportedMediaType]: {
    message: "Dieser Inhalt kann keine Punkte erhalten.",
    severity: "warning",
  },
  [BackendErrorCodesGamification.HistoryImportTooFewLogs]: {
    message: "Dein Verlauf ist noch zu kurz, um importiert zu werden.",
    severity: "warning",
  },
  [BackendErrorCodesGamification.HistoryImportPointsTooHigh]: {
    message: "Deine Reise hat bereits genug Punkte für einen Verlaufsimport.",
    severity: "warning",
  },
  [BackendErrorCodesGamification.CompletedAtOutOfRange]: {
    message: "Der Abschlusszeitpunkt dieser Übung ist ungültig. Bitte versuche es erneut.",
    severity: "warning",
  },
  [BackendErrorCodesGamification.InvalidClientRequestId]: {
    message: "Die Sitzung konnte nicht zugeordnet werden. Bitte starte die Übung erneut.",
    severity: "warning",
  },
  [BackendErrorCodesGamification.WatchedFilesMalformed]: {
    message: "Deine Fortschrittsdaten sind unvollständig. Bitte kontaktiere uns.",
    severity: "danger",
  },
  [BackendErrorCodesGamification.SessionResultMalformed]: {
    message: "Dein Sitzungsergebnis konnte nicht gelesen werden. Bitte versuche es erneut.",
    severity: "danger",
  },

  // Legal errors (contract cancellation & withdrawal flows)
  [BackendErrorCodesLegal.CancellationRequestNotFound]: {
    message: "Diese Kündigungsanfrage wurde nicht gefunden.",
    severity: "warning",
  },
  [BackendErrorCodesLegal.CancellationRequestWrongType]: {
    message: "Ungültige Anfrage.",
    severity: "warning",
  },
  [BackendErrorCodesLegal.CancellationRequestNotConfirmable]: {
    message: "Diese Kündigungsanfrage kann nicht mehr bestätigt werden.",
    severity: "warning",
  },
  [BackendErrorCodesLegal.CancellationRequestProcessing]: {
    message: "Deine Kündigung wird gerade bestätigt. Bitte warte einen Moment und lade die Seite neu.",
    severity: "warning",
  },
  [BackendErrorCodesLegal.CancellationRequiresManualProcessing]: {
    message:
      "Diese Kündigungsanfrage muss manuell bearbeitet werden und kann nicht über den Link abgeschlossen werden.",
    severity: "warning",
  },
  [BackendErrorCodesLegal.ConfirmationLinkExpired]: {
    message: "Der Bestätigungslink ist abgelaufen. Bitte reiche deine Kündigung erneut ein.",
    severity: "warning",
  },
  [BackendErrorCodesLegal.ConfirmationLinkInvalid]: {
    message: "Der Bestätigungslink ist ungültig.",
    severity: "warning",
  },
  [BackendErrorCodesLegal.SessionExpired]: {
    message: "Deine Anmeldung ist abgelaufen. Bitte logge dich erneut ein.",
    severity: "warning",
  },
  [BackendErrorCodesLegal.AccountEmailMissing]: {
    message: "Wir konnten keine E-Mail zu deinem Konto finden. Bitte kontaktiere den Support.",
    severity: "warning",
  },
  [BackendErrorCodesLegal.AgreementDeclined]: {
    message: "Die aktualisierten Rechtstexte wurden für dieses Konto abgelehnt.",
    severity: "warning",
  },
  [BackendErrorCodesLegal.OnboardingAlreadyCompleted]: {
    message: "Das Onboarding wurde bereits abgeschlossen.",
    severity: "warning",
  },
};

export class BackendError extends Error {
  code: BackendErrorCodes;
  severity: AlertTypes;

  /**
   * @param message Developer/log-facing message (stack traces, Sentry, server logs). May be English.
   *   The user-facing text is always derived from `code` via {@link backendErrorMessages}.
   * @param code Enum code that determines the user-facing message and severity.
   * @param severity Alert severity for the client.
   */
  constructor(message: string, code: BackendErrorCodes, severity: AlertTypes = "danger") {
    super(message);
    this.name = "BackendError";
    this.code = code;
    this.severity = severity;

    Object.setPrototypeOf(this, BackendError.prototype);
  }
}

export const getBackendErrorMessageFromCode = (errorCodeString: BackendErrorCodes | string): string => {
  if (Object.hasOwn(backendErrorMessages, errorCodeString)) {
    return backendErrorMessages[errorCodeString as BackendErrorCodes].message;
  }
  return generalErrorText;
};

export const getBackendErrorCodeFromErrorObject = (error: BackendErrorWithCode): BackendErrorCodes => {
  const errorCode = error.code;

  for (const errorEnum of allErrorEnums) {
    if (Object.values(errorEnum).includes(errorCode as BackendErrorCodes)) {
      return errorCode as BackendErrorCodes;
    }
  }
  // If no match is found, return a default error code
  return BackendErrorCodesGeneral.UndefinedError;
};

export const getBackendErrorSeverityFromCode = (errorCode: BackendErrorCodes | string): AlertTypes => {
  if (Object.hasOwn(backendErrorMessages, errorCode)) {
    return backendErrorMessages[errorCode as BackendErrorCodes].severity;
  }
  return "danger";
};

interface BackendErrorWithCode {
  message: string;
  code: string;
}

export const isBackendErrorWithCode = (error: unknown): error is BackendErrorWithCode => {
  return typeof error === "object" && error !== null && "code" in error;
};
